import type { ThemeDefinition } from "../theme";

/** Built-in Light theme — colors only; structure comes from `baseTheme.ts`. */
export const lightTheme: ThemeDefinition = {
  id: "light",
  name: "Light",
  builtIn: true,
  palette: {
    mode: "light",
    primary: { main: "#1447E6" },
    secondary: { main: "#6750A4" },
    error: { main: "#B3261E" },
    background: { default: "#FAFAFA", paper: "#ffffff" },
  },
  appColors: {
    headerLogoColor: "#0B57D0",
    headerBackground: "#0F172B",
    headerNavLinks: { textColor: "#64748b" },
    headerSearch: {
      placeholderColor: "#9CA3AF",
      iconAndTextColor: "#9CA3AF",
      borderColor: "#475569",
      backgroundColor: "#1E293B",
      restingPlaceholderColor: "#6B7280",
      filledTextColor: "#E2E8F0",
      activeColor: "#4C97F0",
      activeRowBackground: "rgba(76, 151, 240, 0.16)",
      cardBackground: "#121A20",
      cardHeaderBackground: "#1A2430",
      cardPlaceholderColor: "#6B7486",
      rowBorderColor: "#1E2A36",
      cusipColor: "#4C97F0",
      nameColor: "#E4E8EF",
      typeColor: "#9AA3B2",
      couponColor: "#E4E8EF",
      yearColor: "#6B7486",
      footerColor: "#4F5869",
      closeColor: "#6B7486",
    },
    dataGrid: {
      secondaryTextColor: "#64748B",
      headerTextColor: "#64748B",
      headerBackgroundColor: "#F9FAFB",
      headerSurfaceColor: "#FAFAFA",
      headerRuleColor: "rgba(0, 0, 0, 0.06)",
      rowBorderColor: "#E5E7EB",
      footerBackgroundColor: "#FFFFFF",
      footerBorderColor: "rgba(0, 0, 0, 0.08)",
      selectionCheckboxColor: "#767676",
      headerSortedTextColor: "#475569",
      cellTextColor: "#0F172A",
      cusipLinkColor: "#3B82F6",
      activeRowBorderColor: "#1447E6",
      activeRowBackgroundColor: "rgba(20, 71, 230, 0.08)",
      watchlistMetrics: {
        offers: "#E8F2FF",
        bids: "#FFF3E0",
        rfqs: "#EAF8EE",
        trades: "#F2EAFE",
      },
      watchlistMetricHeaders: {
        offers: "#E8F2FF",
        bids: "#FFF3E0",
        rfqs: "#EAF8EE",
        trades: "#F2EAFE",
      },
      watchlistMetricInk: {
        offers: { count: "#1D4ED8", quantity: "#2563EB" },
        bids: { count: "#9A3412", quantity: "#9A3412" },
        rfqs: { count: "#15803D", quantity: "#16A34A" },
        trades: { count: "#7E22CE", quantity: "#6B21A8" },
      },
      bondTypeChip: {
        go: { bg: "rgba(59, 130, 246, 0.12)", fg: "#1D4ED8" },
        revenue: { bg: "rgba(16, 185, 129, 0.12)", fg: "#047857" },
        unclassified: "#64748B",
        feature: { border: "#E2E8F0", fg: "#64748B" },
      },
    },
    filterBar: {
      endpointSwitcherBackground: "#F2F2F4",
      tabSwitcherBackground: "#FFFFFF",
      tabSwitcherBorderColor: "rgba(0, 0, 0, 0.23)",
      tabSwitcherTextColor: "#0F172A",
      tabSwitcherIconColor: "rgba(0, 0, 0, 0.54)",
      barLabelColor: "#475569",
      filterTriggerTextColor: "#1D4ED8",
      filterTriggerBorderColor: "rgba(29, 78, 216, 0.5)",
      advancedButtonTextColor: "#FFFFFF",
      filledActionBackgroundColor: "#1D4ED8",
      clearButtonHoverBackground: "#F9FAFB",
    },
    filter: {
      labelColor: "#94A3B8",
      inputTextColor: "#717182",
      inputPlaceholderColor: "#717182",
      inputBackgroundColor: "#F3F3F5",
      wrapperBorderColor: "#E5E7EB",
      wrapperBackgroundColor: "#fff",
      identifierInput: {
        backgroundColor: "#FFFFFF",
        textColor: "#0A0A0A",
        placeholderColor: "rgba(15, 23, 42, 0.5)",
        borderColor: "rgba(0, 0, 0, 0.08)",
        labelColor: "#475569",
        suggestionTextColor: "#0F172A",
        chip: {
          validText: "#193CB8",
          validBackground: "#DBEAFE",
          invalidText: "#C10007",
          invalidBackground: "#FFE2E2",
        },
      },
      numericInput: {
        labelColor: "#475569",
        backgroundColor: "#FFFFFF",
        borderColor: "rgba(0, 0, 0, 0.23)",
        textColor: "#0F172A",
        placeholderColor: "rgba(15, 23, 42, 0.5)",
        checkboxColor: "#475569",
        selectTextColor: "#0F172A",
        selectIconColor: "rgba(0, 0, 0, 0.54)",
      },
      toggleTab: {
        activeColor: "#1D4ED8",
        inactiveColor: "#94A3B8",
        dividerColor: "rgba(0, 0, 0, 0.08)",
      },
    },
    dialog: {
      borderColor: "#314158",
      footerBackground: "#FAFAFA",
      footerBorderColor: "rgba(0, 0, 0, 0.08)",
      footerOutlinedHoverBackground: "#F3F4F6",
      footerPrimaryButtonShadow: "0 1px 2px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1)",
      footerPrimaryButtonBackground: "#1E40AF",
      navBackground: "#F9FAFB",
      navSectionLabelColor: "#94A3B8",
      navItemColor: "#475569",
      navItemHoverBackground: "#F0F0F0",
      navItemActiveColor: "#1E40AF",
      navItemActiveHoverBackground: "rgba(25, 118, 210, 0.08)",
      // Dialog headers stay dark in both themes.
      headerBackground: "#212121",
      headerTextColor: "#FFFFFF",
      headerIconColor: "#D1D5DB",
      headerCloseIconColor: "#E0E0E0",
      headerCloseHoverBackground: "#424242",
      headerSubtitleColor: "rgba(255,255,255,0.7)",
      sectionTitleColor: "#0F172A",
      sectionAccentColor: "#1E40AF",
      listRowLabelColor: "#475569",
      listToggleTextColor: "#475569",
      listToggleBorderColor: "rgba(0, 0, 0, 0.08)",
      listToggleHoverBackground: "#E8E8E8",
      listToggleSuccessBackground: "#16A34A",
      listToggleErrorBackground: "#DC2626",
      listToggleErrorShadow: "0 4px 12px rgba(0, 0, 0, 0.22), 0 1px 3px rgba(0, 0, 0, 0.12)",
      listToggleSelectedTextColor: "#FFFFFF",
      accentTextColor: "#1E40AF",
      deleteIconColor: "#DC2626",
      destructiveButtonTextColor: "#FFFFFF",
    },
    accountMenu: {
      background: "#FFFFFF",
      headerBackground: "#F1F5F9",
      headerHoverBackground: "#E2E8F0",
      dividerColor: "rgba(0, 0, 0, 0.08)",
      primaryTextColor: "#0F172A",
      secondaryTextColor: "#475569",
      iconColor: "rgba(0, 0, 0, 0.54)",
      demoIconColor: "#8B5CF6",
      switchTrackColor: "#CBD5E1",
      logoutColor: "#DC2626",
    },
    makeCorrectionsDialog: {
      optionalLabelColor: "#90A1B9",
      inputTextColor: "#0A0A0A",
      unchangedAccentColor: "#2e7d32",
      changedAccentColor: "#ed6c02",
      editIconColor: "#155DFC",
    },
    messageMetrics: {
      cardShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
      cardBorder: "none",
      toneFillOpacity: 0.07,
      pageBackground: "#F1F5F9",
      sourcesHeaderText: "#475569",
      periodToggle: {
        borderOpacity: 0.08,
        selectedOpacity: 0.08,
        selectedHoverOpacity: 0.12,
        hoverOpacity: 0.04,
      },
      chart: {
        dailyBar: "#93C5FD",
        cumulativeLine: "#4F46E5",
        limitLine: "#EF4444",
        axisLine: "#ECEFF3",
      },
    },
    aiAssist: {
      pageBg: "#FFFFFF",
      sidebarBg: "#F8FAFC",
      cardBg: "#FFFFFF",
      raisedBg: "#F1F5F9",
      headerBg: "#F8FAFC",
      inputBg: "#F8FAFC",
      avatarBg: "#334155",
      border: "#E2E8F0",
      borderSubtle: "#E6EBF1",
      textTitle: "#0F172A",
      textBody: "#1E293B",
      textSecondary: "#64748B",
      textMuted: "#8A97A8",
      textFaint: "#94A3B8",
      textHint: "#CBD5E1",
      textStrong: "#475569",
      textConversation: "#374151",
      onAccent: "#FFFFFF",
      accent: "#3B82F6",
      accentWash: "#F0F7FF",
      accentStrong: "#2563EB",
      userBubbleBg: "#1D4ED8",
      kindBlue: "#60A5FA",
      kindGreen: "#34D399",
      kindAmber: "#F59E0B",
      kindPurple: "#A78BFA",
      kindRed: "#EF4444",
      gradientStart: "#2563EB",
      gradientEnd: "#7C3AED",
      meterGradientStart: "#3B82F6",
      meterGradientEnd: "#8B5CF6",
      fabBadgeBg: "#EF4444",
      fabShadow: "0px 8px 32px rgba(59, 130, 246, 0.45)",
      fabShadowHover: "0px 8px 32px rgba(59, 130, 246, 0.55)",
      fabBadgeShadow: "0px 1px 4px rgba(0, 0, 0, 0.3)",
      buttonShadow: "0 4px 12px rgba(37, 99, 235, 0.28)",
      buttonShadowHover: "0 6px 16px rgba(37, 99, 235, 0.35)",
      selectedBg: "rgba(37, 99, 235, 0.08)",
      selectedBgHover: "rgba(37, 99, 235, 0.12)",
      composerShadow: "0 8px 28px rgba(15, 23, 42, 0.08)",
      composerActiveShadow: "0 8px 28px rgba(59, 130, 246, 0.28)",
      bubbleShadow: "0px 1px 6px rgba(0, 0, 0, 0.06)",
      chart: {
        axis: "#8A97A8",
        grid: "#E6EBF1",
        title: "#8A97A8",
        markFill: "#FFFFFF",
        preBg: "#F8FAFC",
        series: ["#3B82F6", "#0F9D63", "#9AA3AF", "#60A5FA", "#CBD5E1"],
      },
      bondCard: {
        border: "#E6EBF1",
        rowBorder: "#F1F4F8",
        statsBg: "#F8FAFC",
        headerBg: "#0C1A2B",
        headerText: "rgba(255, 255, 255, 0.85)",
        cusip: "#5AA2FF",
        value: "#0F172A",
        label: "#8A97A8",
        positive: "#0F9D63",
        qtyValue: "#334155",
        ratingBg: "#EAF3EA",
        ratingText: "#4A7A4E",
        stateBg: "rgba(100, 116, 139, 0.15)",
        stateText: "#64748B",
        taxBg: "rgba(34, 197, 94, 0.15)",
        taxText: "#16A34A",
        buyBg: "rgba(15, 157, 99, 0.2)",
        buyBorder: "rgba(15, 157, 99, 0.4)",
      },
    },
  },
};
